import type { CampaignReportResponse, CampaignSummary } from "../../types";
import { formatReportDateLabel } from "../../utils/dateFormatting";

export interface CompactBrandRoasDatum {
  brand: string;
  roas: number;
  sales: number;
  spend: number;
}

export interface CompactSalesMixDatum {
  brand_name: string;
  units: number;
  percentage: number;
}

export interface CompactChartDataset {
  label: string;
  data: number[];
  backgroundColor: string | string[];
}

export interface CompactLayoutViewModel {
  totalSales: number;
  totalSpend: number;
  avgROAS: number;
  salesGrowthRate: number;
  avgDailySales: number;
  avgDailySpend: number;
  topCampaigns: CampaignSummary[];
  brandROAS: CompactBrandRoasDatum[];
  salesMixData: CompactSalesMixDatum[];
  dailyLabels: string[];
  dailyChartData: CompactChartDataset[];
  roasLabels: string[];
  roasValues: number[];
}

export const buildCompactLayoutViewModel = (
  data: CampaignReportResponse,
): CompactLayoutViewModel => {
  const { brands, campaigns, daily_metrics, summary } = data;
  const totalSales = parseFloat(summary.total_sales);
  const totalSpend = parseFloat(summary.total_spend);
  const avgROAS = parseFloat(summary.average_roas);
  const firstDaySales = parseFloat(daily_metrics[0].total_sales);
  const salesGrowthRate =
    daily_metrics.length > 1 && firstDaySales !== 0
      ? ((parseFloat(daily_metrics[daily_metrics.length - 1].total_sales) -
          firstDaySales) /
          firstDaySales) *
        100
      : 0;
  const avgDailySales = totalSales / summary.total_days;
  const avgDailySpend = totalSpend / summary.total_days;
  const topCampaigns = [...campaigns]
    .sort((a, b) => parseFloat(b.total_sales) - parseFloat(a.total_sales))
    .slice(0, 3);
  const brandROAS = brands
    .map((brand) => ({
      brand: brand.brand,
      roas: parseFloat(brand.roas),
      sales: parseFloat(brand.total_sales),
      spend: parseFloat(brand.total_spend),
    }))
    .sort((a, b) => b.roas - a.roas);
  const totalBrandSales = brands.reduce(
    (sum, brand) => sum + parseFloat(brand.total_sales),
    0,
  );
  const salesMixData = brands.map((brand) => ({
    brand_name: brand.brand,
    units: parseFloat(brand.total_sales),
    percentage:
      totalBrandSales > 0
        ? (parseFloat(brand.total_sales) / totalBrandSales) * 100
        : 0,
  }));
  const dailyLabels = daily_metrics.map((day) =>
    formatReportDateLabel(day.report_date),
  );
  const dailyChartData = [
    {
      label: "Sales",
      data: daily_metrics.map((day) => parseFloat(day.total_sales)),
      backgroundColor: "rgba(59, 130, 246, 0.9)",
    },
    {
      label: "Spend",
      data: daily_metrics.map((day) => parseFloat(day.total_spend)),
      backgroundColor: "rgba(239, 68, 68, 0.9)",
    },
  ];

  return {
    totalSales,
    totalSpend,
    avgROAS,
    salesGrowthRate,
    avgDailySales,
    avgDailySpend,
    topCampaigns,
    brandROAS,
    salesMixData,
    dailyLabels,
    dailyChartData,
    roasLabels: brandROAS.map((brand) => brand.brand),
    roasValues: brandROAS.map((brand) => brand.roas),
  };
};
